import { useCallback, useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { SortDescriptor } from '@progress/kendo-data-query'
import { Grid, GridCellProps, GridColumn, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import {
  Email,
  EmailPreview,
  EmailPreviewRequest,
} from 'api/Trading-Accounts/Types/EmailsDetailsDto'
import { Pager } from 'components/components/Pager'
import { Modal } from 'components/CustomModal'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { errorToast, successToast, ToastContext } from 'components/Toast/Toast'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { normalizeWalletsSortQuery } from 'routes/Records/Wallets/normalizer'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchClientLanguagesAction } from 'store/clients/clients.actions'
import { CompositeFilterDescriptor, GridFilterActionState } from 'store/grid'
import { fetchCountriesAction, fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { IdentityApiClient } from 'utils/api/identityApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { emailColumns } from './emailColumns'
import { EmailDataModal, EmailPreviewValues } from './EmailDataModal'
import { getEmailFilterFields } from './emailFields'
import { EmailPreviewModal } from './EmailPreviewModal'

export const Emails = () => {
  const { gridState, gridActions } = useGridState()
  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)
  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [emails, setEmails] = useState<Email[]>([])
  const [previewModal, setPreviewModal] = useState<Email>()
  const [generatedEmail, setGeneratedEmail] = useState<EmailPreview>()
  const [emailData, setEmailData] = useState<EmailPreviewRequest>()
  const [cachedValues, setCachedValues] = useState<EmailPreviewValues>()
  const permissions = auth?.permissions || []

  const countries = useAppSelector((state) => state.identity.countries)
  const languages = useAppSelector((state) => state.clients.language)
  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (isEmpty(languages)) dispatch(fetchClientLanguagesAction(clientsApiClient))
    if (isEmpty(countries)) dispatch(fetchCountriesAction(identityApiClient))
    if (isEmpty(tickmillCompanies)) dispatch(fetchTickmillCompaniesAction(identityApiClient))
  }, [dispatch, languages, countries, tickmillCompanies])

  const [selectedEmail, setSelectedEmail] = useState<Email>()

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => buildQuery(filter ?? gridState.filter) ?? null,
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(normalizeWalletsSortQuery(sortDescriptors ?? gridState.sort))
    },
    [gridState.sort]
  )

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterState: GridFilterActionState): Promise<PageData<Email[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await clientAreaApiClient.getEmails({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.take,
            skipCount: tableData?.skipCount !== false,
          })

          if (response?.items) {
            setEmails(response.items.map((x) => Object.assign({ selected: false }, x)))
          }

          setTableData({ ...tableData, meta: response })
          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<Email[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const filterFetchEmails = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSort(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await clientAreaApiClient.getEmails({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setEmails(response.items.map((x) => Object.assign({ selected: false }, x)))
      }

      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // TODO: update clientsAPIClient so that we can include it in the dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getQuery,
      getSort,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await clientAreaApiClient.getEmails({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setEmails(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await clientAreaApiClient.getEmails({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setEmails(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page

    gridActions.setGridFilters({
      pageIndex,
    })
    const response = await clientAreaApiClient.getEmails({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })

    if (response?.items) {
      setEmails(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await clientAreaApiClient.getEmails({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setEmails(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleGeneratePreview = async (
    data: EmailPreviewRequest,
    rawValues: EmailPreviewValues
  ) => {
    setCachedValues(rawValues)
    const response = await clientAreaApiClient.getEmailPreview(data)
    setEmailData(data)
    setGeneratedEmail(response)
    setPreviewModal(selectedEmail)
    setSelectedEmail(undefined)
    setCachedValues(undefined)
  }

  const handleSendEmail = async (cc: string[]) => {
    if (!emailData) return
    try {
      await clientAreaApiClient.sendEmail({ ...emailData, cc })
      setToast(successToast('Test email sent successfully.'))
    } catch (error: unknown) {
      setToast(errorToast('Failed to send email.' + error))
    } finally {
      setPreviewModal(undefined)
      setEmailData(undefined)
      setGeneratedEmail(undefined)
    }
  }

  const clearCacheCall = (func: () => void) => {
    setCachedValues(undefined)
    return func()
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <>
      {!!previewModal && !!generatedEmail && (
        <Modal
          visible={!!previewModal && !!generatedEmail}
          onClose={() => clearCacheCall(() => setPreviewModal(undefined))}
        >
          <EmailPreviewModal
            goBack={() => {
              setSelectedEmail(previewModal)
              setPreviewModal(undefined)
            }}
            preview={generatedEmail}
            emaildata={previewModal}
            sendEmail={handleSendEmail}
          />
        </Modal>
      )}
      {selectedEmail && (
        <EmailDataModal
          values={cachedValues}
          languages={languages}
          tickmillCompanies={tickmillCompanies}
          countries={countries}
          onSubmit={handleGeneratePreview}
          onCancel={() => clearCacheCall(() => setSelectedEmail(undefined))}
          data={selectedEmail}
          me={auth?.user}
        />
      )}
      <Breadcrumbs crumbs={[{ title: 'Emails', link: links.emails.url }]} />
      <SelectFiltersFacade
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetchEmails}
        filterType={FilterType.Emails}
        gridState={gridState}
        filterFields={getEmailFilterFields()}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport data={emails} columns={emailColumns(gridState)}>
          <Grid
            {...gridState}
            resizable
            data={emails}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            pageable
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {emailColumns(gridState)
              .filter((c) => c.show)
              .map((column, idx) => (
                <GridColumn key={idx + 2} {...column} />
              ))}
            <GridColumn
              width={80}
              cell={({ dataItem }: GridCellProps) => (
                <td>
                  <MoreButton
                    items={[
                      {
                        name: 'Preview',
                        onClick: () => setSelectedEmail(dataItem),
                        disabled: !hasPermission(permissions, PermissionsEnum.CanPreviewEmails),
                      },
                    ]}
                  />
                </td>
              )}
            />
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </>
  )
}
